import { Observe } from 'core/decorators/observe';
import { Define, Light } from '../../../core/decorators';
import { TAG_COLUMN } from '../../../core/utils/constants/tags';
import { Nullable, retry, UI_TEN, Uint } from '@rotomeca/utils';
import { ValueOf } from '../../../core/utils/types';
import BnumElement, { CustomDom } from '../../bnum-element';
import {
	ATTRIBUTES,
	CLASSES,
	DEFAULT_COLUMN_TYPE,
	SLOTS,
} from './bnum-column.internal/constants';

//#region Types
/**
 * Constantes représentant les slots possibles d'une colonne.
 */
export const ColumnSlot = {
	HEADER: 'header',
	FOOTER: 'footer',
	BODY: 'body',
} as const;

/**
 * Type représentant les slots possibles d'une colonne.
 * - 'header' : pour l'en-tête
 * - 'body' : pour le corps
 * - 'footer' : pour le pied de page
 */
export type ColumnSlot = ValueOf<typeof ColumnSlot>;
//#endregion Types

/**
 *  Permet de structurer une colonne avec un en-tête, un corps et un pied de page.
 *
 * @structure Colonne
 * <bnum-column>
 *  <div slot="header">En-tête de la colonne</div>
 *   <div>Contenu principal de la colonne</div>
 *  <div slot="footer">Pied de page de la colonne</div>
 * </bnum-column>
 *
 * @attr {string} (optional) (default: 'default') type - Le type de colonne (ex: "sidebar", "main", "tools")
 */
@Define({ tag: TAG_COLUMN })
@Light()
@Observe(ATTRIBUTES.TYPE)
export class HTMLBnumColumn extends BnumElement {
	//#region Getters/Setters
	/**
	 * Permet de définir le type de colonne (ex: "sidebar", "main", "tools")
	 * Utile pour le CSS qui va définir la largeur
	 */
	get type(): string {
		return this.getAttribute(ATTRIBUTES.TYPE) || DEFAULT_COLUMN_TYPE;
	}

	/**
	 * Indique si le corps de la colonne doit être conservé lors de certaines opérations.
	 *
	 * Rappel: data- ne sert qu'à stocker des informations avant la création du composant.
	 */
	get #_keepBody(): boolean {
		return this.data(ATTRIBUTES.DATA.KEEP_BODY) === 'true';
	}
	//#endregion Getters/Setters

	//#region LifeCycle
	/**
	 * Logique de rendu Light DOM
	 * On récupère les enfants existants et on les réorganise.
	 * @param container Le conteneur dans lequel injecter le DOM reconstruit
	 * @protected
	 */
	protected _p_buildDOM(container: CustomDom): void {
		if (this.childNodes.length === 0) {
			retry(
				async () => {
					if (this.childNodes.length === 0) throw new Error('No children yet');
					this.#_buildDOM(container);
				},
				Uint[5],
				Uint[100],
			).catch(() => {
				if (!this.data('tempted')) {
					this.data('tempted', 'ok');
					setTimeout(() => this._p_buildDOM(container), 500);
				} else {
					console.error(
						'### Impossible de mettre en place le composant !',
						this,
						container,
					);
				}
			});
			return;
		}

		this.#_buildDOM(container);
	}

	/**
	 * Logique de rendu Light DOM
	 * On récupère les enfants existants et on les réorganise.
	 * @param container Le conteneur dans lequel injecter le DOM reconstruit
	 * @private
	 */
	#_buildDOM(container: CustomDom): void {
		// Sauvegarde des enfants actuels
		const originalChildren = Array.from(this.childNodes);

		// Fragment temporaire pour construire le DOM avant injection
		const fragment = document.createDocumentFragment();

		// Création des conteneurs
		const [headerContainer, bodyContainer, footerContainer] =
			this._p_createDivs(
				{
					classes: [CLASSES.HEADER.MAIN, CLASSES.HEADER.RC],
				},
				{
					classes: [CLASSES.BODY],
				},
				{
					classes: [CLASSES.FOOTER.MAIN, CLASSES.FOOTER.RC],
				},
			);

		// Distribution des enfants (Slotting manuel)
		let hasHeader = false;
		let hasFooter = false;

		for (const node of originalChildren) {
			// Si c'est un noeud texte vide, on ignore
			if (node.nodeType === Node.TEXT_NODE) {
				if (node.textContent?.trim()) bodyContainer.appendChild(node);
				continue;
			}

			const nodeElement: Nullable<HTMLElement> =
				node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : null;

			if (!nodeElement) continue;

			const slotName = nodeElement.getAttribute
				? nodeElement.getAttribute('slot')
				: null;

			switch (slotName) {
				case SLOTS.HEADER:
					this.#_processNode(nodeElement, CLASSES.CONTENT.HEADER);
					headerContainer.appendChild(node);

					if (!hasHeader) hasHeader = true;
					break;

				case SLOTS.FOOTER:
					this.#_processNode(nodeElement, CLASSES.CONTENT.FOOTER);
					footerContainer.appendChild(node);

					if (!hasFooter) hasFooter = true;
					break;
				default:
					this.#_processNode(nodeElement, CLASSES.CONTENT.BODY);
					bodyContainer.appendChild(node);
					break;
			}
		}

		// Nettoyage du container principal
		while (container.firstChild) {
			container.removeChild(container.firstChild);
		}

		// Ajout des classes principales
		this.classList.add(CLASSES.HOST, `${CLASSES.CONTENT_PREFIX}--${this.type}`);

		// Injection conditionnelle dans le DOM
		if (hasHeader) fragment.appendChild(headerContainer);

		if (this.#_keepBody) fragment.appendChild(bodyContainer);
		else fragment.append(...Array.from(bodyContainer.childNodes));

		if (hasFooter) fragment.appendChild(footerContainer);

		container.appendChild(fragment);
	}

	/**
	 * Reactivity for Type attribute change
	 */
	protected _p_update(
		name: string,
		oldVal: Nullable<string>,
		newVal: Nullable<string>,
	): void {
		if (oldVal === newVal) return;

		if (name === ATTRIBUTES.TYPE && this.alreadyLoaded) {
			if (oldVal) this.classList.remove(`${CLASSES.CONTENT_PREFIX}--${oldVal}`);
			if (newVal) this.classList.add(`${CLASSES.CONTENT_PREFIX}--${newVal}`);
		}
	}
	//#endregion LifeCycle

	//#region Méthodes privées
	/**
	 * Traite un élément enfant : supprime l'attribut slot, ajoute les classes CSS nécessaires,
	 * et gère la rétrocompati<zbilité des classes "header".
	 * @param {HTMLElement} element L'élément à traiter
	 * @param {string} specificClass Classe CSS spécifique à ajouter
	 * @private
	 */
	#_processNode(element: HTMLElement, specificClass: string): void {
		element.removeAttribute('slot');
		element.classList.add(specificClass, CLASSES.FROM_SLOT);

		// Gestion legacy "header" class duplication
		if (element.classList.contains(CLASSES.HEADER.RC)) {
			element.classList.remove(CLASSES.HEADER.RC);
			element.classList.add(CLASSES.HEADER.OLD);
		}
	}
	//#endregion Méthodes privées
}
